/**
 * Case-number (sagsnummer) formatting, shared by web, app and the Settings page so
 * the number a user is shown is the same one the server will allocate.
 *
 * The authority is the backend: `ProjectNumberAllocator.AllocateStandaloneAsync`
 * composes `{prefix}{counter:D3}` from the org's `CaseNumberConfig`, consuming and
 * bumping the counter inside an advisory lock. The `prefix` already carries the year
 * segment (default `"2026-"`), so it is used verbatim — a company that wants
 * `SAG-2026-` just configures that instead.
 *
 * Anything rendered from this is a *hint*, never a reservation: the counter is only
 * consumed when a project is actually created, and the allocator skips values already
 * taken by migrated projects. Two people opening the create dialog therefore see the
 * same suggestion, and both still get distinct numbers on save.
 */

/** Lowest number the server will accept or store (`nextNumber >= 1`). */
const NEXT_NUMBER_FLOOR = 1;

/** Zero-padding width; matches the backend's `:D3`. */
const PAD_WIDTH = 3;

/**
 * Formats a prefix + next-number into the backend's `{prefix}{n:000}` shape.
 *
 * A blank or invalid `nextNumber` falls back to the floor of 1, mirroring the server's
 * own invariant. Numbers wider than the pad are not truncated — an org that has passed
 * 999 keeps counting rather than silently wrapping.
 */
export function formatCaseNumber(prefix: string, nextNumber: number | string): string {
  const parsed = typeof nextNumber === 'number' ? nextNumber : parseInt(nextNumber, 10);
  const floor = Number.isFinite(parsed) && parsed >= NEXT_NUMBER_FLOOR ? parsed : NEXT_NUMBER_FLOOR;
  return `${prefix}${String(floor).padStart(PAD_WIDTH, '0')}`;
}
